import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { errorAlert } from '@components/toastify/notify-toast';
import { AIRFLOW_BASE_PATH, DEFAULT_RUNS_CHART_LIMIT, DEFAULT_TIME_RANGE, FAILED_TASK_LOGS_LIMIT, MAP_INDEX_NONE, TIME_RANGE_OPTIONS } from '../../constants';
import { AirflowDagRun, AirflowTaskInstance } from '../../types';
import { bucketTimestamps, hoursAgoRange } from '../../utils';
import { ChartRun } from './runs-duration-chart';

/*
 * Manages the DAG Overview tab: failed-run/failed-task stat cards, the run-duration chart,
 * and the recent-failed-tasks list with its Clear Task dialog
 */
export function useOverviewTab(dagId: string, refreshKey: number) {
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const history = useHistory();

    const [timeRange, setTimeRange] = useState(DEFAULT_TIME_RANGE);
    const [runsChartLimit, setRunsChartLimit] = useState<number>(DEFAULT_RUNS_CHART_LIMIT);
    const [failedRunsTotal, setFailedRunsTotal] = useState(0);
    const [failedTasksTotal, setFailedTasksTotal] = useState(0);
    const [failedRunsSeries, setFailedRunsSeries] = useState<{ index: number; count: number }[]>([]);
    const [failedTasksSeries, setFailedTasksSeries] = useState<{ index: number; count: number }[]>([]);
    const [recentFailedTasks, setRecentFailedTasks] = useState<AirflowTaskInstance[]>([]);
    const [chartRuns, setChartRuns] = useState<ChartRun[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [clearDialogTask, setClearDialogTask] = useState<AirflowTaskInstance | null>(null);

    const range = useMemo(() => {
        const hours = TIME_RANGE_OPTIONS.find((option) => option.value === timeRange)?.hours ?? 24;
        return hoursAgoRange(hours);
    }, [timeRange]);

    /*
     * Fetches failed runs and failed task instances for the selected time range
     */
    const fetchFailedMetrics = useCallback(async () => {
        try {
            const [runsResponse, tasksResponse] = await Promise.all([
                AirflowDashboardHandler.listDagRuns(Env, dagId, { limit: 100, state: ['failed'], runAfterGte: range.gte, runAfterLte: range.lte, order_by: '-execution_date' }),
                AirflowDashboardHandler.listDagTaskInstances(Env, dagId, { limit: 100, state: ['failed'], runAfterGte: range.gte, runAfterLte: range.lte, order_by: '-execution_date' }),
            ]);
            setFailedRunsTotal(runsResponse.total_entries ?? runsResponse.dag_runs.length);
            setFailedTasksTotal(tasksResponse.total_entries ?? tasksResponse.task_instances.length);
            setFailedRunsSeries(bucketTimestamps(runsResponse.dag_runs.map((r) => r.execution_date ?? r.run_after), range.gte, range.lte));
            setFailedTasksSeries(bucketTimestamps(tasksResponse.task_instances.map((t) => t.execution_date ?? t.run_after), range.gte, range.lte));
            setRecentFailedTasks(tasksResponse.task_instances.slice(0, FAILED_TASK_LOGS_LIMIT));
        } catch {
            errorAlert('Failed to load Overview metrics.');
        }
    }, [Env, dagId, range]);

    /*
     * Fetches the last N runs for the duration chart, oldest-first
     */
    const fetchChartRuns = useCallback(async () => {
        try {
            const response = await AirflowDashboardHandler.listDagRuns(Env, dagId, { limit: runsChartLimit, order_by: '-execution_date' });
            const runs: ChartRun[] = response.dag_runs
                .slice()
                .reverse()
                .map((run: AirflowDagRun) => {
                    const startMs = run.start_date ? new Date(run.start_date).getTime() : undefined;
                    const endMs = run.end_date ? new Date(run.end_date).getTime() : undefined;
                    const durationSec = run.duration ?? (startMs && endMs ? (endMs - startMs) / 1000 : startMs ? (Date.now() - startMs) / 1000 : 0);
                    return { runId: run.dag_run_id, label: run.execution_date ?? run.run_after ?? run.start_date, durationSec, state: run.state };
                });
            setChartRuns(runs);
        } catch {
            errorAlert('Failed to load run duration chart.');
        }
    }, [Env, dagId, runsChartLimit]);

    useEffect(() => {
        setIsLoading(true);
        Promise.all([fetchFailedMetrics(), fetchChartRuns()]).finally(() => setIsLoading(false));
    }, [fetchFailedMetrics, fetchChartRuns, refreshKey]);

    /*
     * Navigates to a task instance's full log view
     */
    const handleViewFullLogs = useCallback(
        (task: AirflowTaskInstance) => {
            const mapIndexQuery = task.map_index !== undefined && task.map_index !== MAP_INDEX_NONE ? `?map_index=${task.map_index}` : '';
            history.push(`${AIRFLOW_BASE_PATH}/${encodeURIComponent(dagId)}/runs/${encodeURIComponent(task.dag_run_id)}/tasks/${encodeURIComponent(task.task_id)}${mapIndexQuery}`);
        },
        [history, dagId]
    );

    /*
     * Navigates to a run's detail page
     */
    const handleRunClick = useCallback(
        (runId: string) => {
            history.push(`${AIRFLOW_BASE_PATH}/${encodeURIComponent(dagId)}/runs/${encodeURIComponent(runId)}`);
        },
        [history, dagId]
    );

    /*
     * Re-fetches Overview metrics after a task is cleared
     */
    const handleTaskCleared = useCallback(() => {
        fetchFailedMetrics();
    }, [fetchFailedMetrics]);

    return {
        timeRange,
        setTimeRange,
        runsChartLimit,
        setRunsChartLimit,
        failedRunsTotal,
        failedTasksTotal,
        failedRunsSeries,
        failedTasksSeries,
        recentFailedTasks,
        chartRuns,
        isLoading,
        clearDialogTask,
        setClearDialogTask,
        handleViewFullLogs,
        handleRunClick,
        handleTaskCleared,
    };
}
